/* Preferences are not prices, inventory, manufacturing specifications or orders. */
(() => {
  if (customElements.get('oakinda-create')) return;
  const wellFormedText = (text) =>
    typeof text.toWellFormed === 'function'
      ? text.toWellFormed()
      : text.replace(/[\uD800-\uDBFF][\uDC00-\uDFFF]|[\uD800-\uDFFF]/g, (sequence) =>
          sequence.length === 2 ? sequence : '\uFFFD',
        );
  customElements.define(
    'oakinda-create',
    class extends HTMLElement {
      connectedCallback() {
        if (this.controller) return;
        this.controller = new AbortController();
        this.panels = [...this.querySelectorAll('[data-panel]')];
        this.fields = [...this.querySelectorAll('[data-brief]')];
        this.step = 0;
        const listen = (node, event, handler) =>
          node?.addEventListener(event, handler, { signal: this.controller.signal });
        listen(this.querySelector('[data-next]'), 'click', () => {
          if (this.validatePanel()) this.showStep(this.step + 1, true);
        });
        listen(this.querySelector('[data-back]'), 'click', () =>
          this.showStep(this.step - 1, true),
        );
        listen(this.querySelector('[data-restart]'), 'click', () => this.showStep(0, true));
        for (const field of this.fields) listen(field, 'input', () => this.updateSummary());
        listen(this.querySelector('[data-photo]'), 'change', (event) =>
          this.previewPhoto(event.target.files?.[0]),
        );
        listen(this.querySelector('[data-remove-photo]'), 'click', () => {
          this.clearPhoto();
          this.querySelector('[data-photo]').focus();
        });
        this.querySelector('[data-progress]').hidden = false;
        this.querySelector('[data-step-actions]').hidden = false;
        this.updateSummary();
        this.showStep(this.querySelector('[data-form-result]') ? 2 : 0);
      }
      disconnectedCallback() {
        this.controller?.abort();
        this.controller = null;
        this.clearPhoto();
      }
      validatePanel() {
        const invalid = [...this.panels[this.step].querySelectorAll('input,select,textarea')].find(
          (field) => !field.checkValidity(),
        );
        if (!invalid) return true;
        invalid.reportValidity();
        invalid.focus();
        return false;
      }
      showStep(step, focus = false) {
        this.step = Math.max(0, Math.min(this.panels.length - 1, step));
        this.panels.forEach((panel, index) => {
          panel.hidden = index !== this.step;
        });
        this.querySelectorAll('[data-step-label]').forEach((label, index) => {
          if (index === this.step) label.setAttribute('aria-current', 'step');
          else label.removeAttribute('aria-current');
        });
        this.querySelector('[data-back]').hidden = this.step === 0;
        this.querySelector('[data-next]').hidden = this.step === 2;
        this.querySelector('[data-restart]').hidden = this.step !== 2;
        this.querySelector('[data-intake-panel]').hidden = this.step !== 2;
        this.querySelector('[data-step-status]').textContent = `Step ${this.step + 1} of 3`;
        this.updateSummary();
        if (focus) this.panels[this.step].querySelector('legend').focus();
      }
      preferences() {
        return this.fields.map((field) => [
          field.dataset.brief,
          field.value.trim() || 'Not specified',
        ]);
      }
      updateSummary() {
        const summary = this.querySelector('[data-summary]');
        const fragment = document.createDocumentFragment();
        for (const [label, value] of this.preferences()) {
          const term = document.createElement('dt');
          const detail = document.createElement('dd');
          term.textContent = label;
          detail.textContent = value;
          fragment.append(term, detail);
        }
        summary.replaceChildren(fragment);
        const download = this.querySelector('[data-download-brief]');
        if (download) {
          const text = [
            'Oakinda Create — Design brief',
            '',
            'Personal planning copy. Not a quote, order or manufacturing approval.',
            'Nothing has been submitted. Room photos and contact details are not included.',
            '',
            ...this.preferences().map(([label, value]) => `${label}: ${value}`),
            '',
            'Materials, price, dimensions, lead time and delivery require human review.',
          ].join('\n');
          download.setAttribute(
            'href',
            `data:text/plain;charset=utf-8,${encodeURIComponent(wellFormedText(text))}`,
          );
          download.hidden = false;
        }
      }
      clearPhoto() {
        this.photoGeneration = (this.photoGeneration || 0) + 1;
        if (this.photoUrl) URL.revokeObjectURL(this.photoUrl);
        this.photoUrl = null;
        const image = this.querySelector('[data-photo-preview]');
        if (!image) return;
        image.removeAttribute('src');
        image.hidden = true;
        this.querySelector('[data-photo-placeholder]').hidden = false;
        this.querySelector('[data-photo]').value = '';
        this.querySelector('[data-remove-photo]').hidden = true;
        this.querySelector('[data-photo-error]').textContent = '';
      }
      previewPhoto(file) {
        this.clearPhoto();
        if (!file) return;
        const error = this.querySelector('[data-photo-error]');
        if (
          !['image/jpeg', 'image/png', 'image/webp'].includes(file.type) ||
          file.size > 8 * 1024 * 1024 ||
          file.size === 0
        ) {
          error.textContent = 'Choose a JPEG, PNG or WebP image smaller than 8 MB.';
          return;
        }
        const generation = this.photoGeneration;
        const url = URL.createObjectURL(file);
        this.photoUrl = url;
        const probe = new Image();
        probe.onload = () => {
          if (generation !== this.photoGeneration) return;
          if (probe.naturalWidth * probe.naturalHeight > 24000000) {
            this.clearPhoto();
            error.textContent = 'Please use a smaller image, up to 24 megapixels.';
            return;
          }
          const image = this.querySelector('[data-photo-preview]');
          image.src = url;
          image.hidden = false;
          this.querySelector('[data-photo-placeholder]').hidden = true;
          this.querySelector('[data-remove-photo]').hidden = false;
        };
        probe.onerror = () => {
          if (generation !== this.photoGeneration) return;
          this.clearPhoto();
          error.textContent = 'This image could not be opened. Try a different JPEG, PNG or WebP.';
        };
        probe.src = url;
      }
    },
  );
})();
